// Avoid circular import by not importing appStore directly
export const getRecaptchaKey = () => {
	// Try to get from global window first (set by app initialization)
	if (window.__OPUS_APP_CONFIG__?.recaptchaKey) {
		return window.__OPUS_APP_CONFIG__.recaptchaKey
	}

	// Fallback to environment variable
	return process.env.REACT_APP_RECAPTCHA_KEY
}

export const RECAPTCHA_CLIENT_KEY = process.env.REACT_APP_RECAPTCHA_KEY // Fallback for immediate use
export const getGoogleRecaptchaUrl = () => `https://www.google.com/recaptcha/api.js?render=${getRecaptchaKey()}`

// Check if reCAPTCHA is ready for use
export const isRecaptchaReady = () => {
	return !!document.getElementById('recaptcha-key') && !!window.grecaptcha && !!window.grecaptcha.ready
}

// Safe wrapper for executing reCAPTCHA
export const executeRecaptcha = async (action) => {
	// Check if script is loaded, if not, load it
	if (!document.getElementById('recaptcha-key')) {
		await handleLoadReCaptcha()
	}

	// Wait for reCAPTCHA to be ready with timeout
	const waitForRecaptcha = async (maxWait = 15000) => {
		const startTime = Date.now()

		while (Date.now() - startTime < maxWait) {
			if (isRecaptchaReady()) {
				return true
			}
			await new Promise((resolve) => setTimeout(resolve, 100))
		}

		throw new Error('reCAPTCHA timeout: Script not ready after 15 seconds')
	}

	// Ensure reCAPTCHA is ready
	await waitForRecaptcha()

	return new Promise((resolve, reject) => {
		window.grecaptcha.ready(async () => {
			try {
				const token = await window.grecaptcha.execute(getRecaptchaKey(), { action })
				resolve(token)
			} catch (error) {
				reject(error)
			}
		})
	})
}

export const handleLoadReCaptcha = () => {
	return new Promise((resolve, reject) => {
		const scriptId = 'recaptcha-key'
		const scriptUrl = getGoogleRecaptchaUrl()

		// Remove existing script if it exists with different URL
		const existingScript = document.getElementById(scriptId)
		if (existingScript && existingScript.src !== scriptUrl) {
			existingScript.remove()
		}

		// Check if already loaded and ready
		if (isRecaptchaReady()) {
			resolve()
			return
		}

		// Only load if not already present
		const script = document.getElementById(scriptId)
		if (!script) {
			const newScript = document.createElement('script')
			newScript.type = 'text/javascript'
			newScript.src = scriptUrl
			newScript.id = scriptId

			newScript.onload = () => {
				// Wait for grecaptcha to be available
				const checkReady = () => {
					if (isRecaptchaReady()) {
						resolve()
					} else {
						setTimeout(checkReady, 100)
					}
				}
				checkReady()
			}

			newScript.onerror = () => {
				reject(new Error('Failed to load reCAPTCHA script'))
			}

			document.body.appendChild(newScript)
		} else {
			// Script exists, wait for it to be ready
			const checkReady = () => {
				if (isRecaptchaReady()) {
					resolve()
				} else {
					setTimeout(checkReady, 100)
				}
			}
			checkReady()
		}
	})
}
